import { useState } from "react";
import { FilterIcon, GroupIcon, MoreHorizontalIcon } from "lucide-react";

import {
  FilterSubMenuMultiselect,
  Row,
  TableOrderByDropdown,
} from "@/components";
import { ConfirmationDialog } from "@/components/ConfirmationDialog";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { useTaggableUsers } from "@/contexts/TaggableUsersContext";
import { useSuccessToast } from "@/hooks/useSuccessToast";
import { useExportJobTracker } from "@/hooks/mutations/useExportJobTracker";
import { useExportJobTrackerAllJobs } from "@/hooks/mutations/useExportJobTrackerAllJobs";
import { useExportRequisitions } from "@/hooks/mutations/useExportRequisitions";
import {
  jobBottleneckOptions,
  jobDifficultyOptions,
  jobHealthOptions,
  jobStatusOptions,
  jobUrgencyOptions,
  requisitionStatusOptions,
} from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { JobQueryParams, JobTrackerGroupBy, JobTrackerSort } from "@/types";

// * IMPORTANT: groupByOptions values should be aligned with the enum values in admin-jobs.service.ts in guac
const groupByOptions = [
  { label: "Owner", value: JobTrackerGroupBy.RequisitionOwner },
  { label: "Requisition Status", value: JobTrackerGroupBy.RequisitionStatus },
];

// * IMPORTANT: sortOptions should be kept in sync with the enum at the top of admin-jobs.service.ts in guac
const sortOptions = [
  { label: "Company Name", value: JobTrackerSort.CompanyName },
  { label: "Created At", value: JobTrackerSort.CreatedAt },
  { label: "Due Date", value: JobTrackerSort.DueDate },
  { label: "Last Update", value: JobTrackerSort.LastUpdate },
  { label: "Revenue", value: JobTrackerSort.Revenue },
];

export const JobTrackerTableQueryOptions = ({
  handleUpdateView,
  jobQueryParams,
}: {
  handleUpdateView: (jobQueryParams: JobQueryParams) => void;
  jobQueryParams: JobQueryParams;
}) => {
  const exportJobTracker = useExportJobTracker();
  const exportJobTrackerAllJobs = useExportJobTrackerAllJobs();
  const exportRequisitions = useExportRequisitions();
  const { userOptions } = useTaggableUsers();
  const successToast = useSuccessToast();

  const [
    isExportJobTrackerConfirmationDialogOpen,
    setIsExportJobTrackerConfirmationDialogOpen,
  ] = useState(false);
  const [
    isExportJobTrackerAllJobsConfirmationDialogOpen,
    setIsExportJobTrackerAllJobsConfirmationDialogOpen,
  ] = useState(false);
  const [
    isExportRequisitionsConfirmationDialogOpen,
    setIsExportRequisitionsConfirmationDialogOpen,
  ] = useState(false);

  const render = () => {
    return (
      <Row className="items-center gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button className="h-8" variant="outlineSecondary">
              <FilterIcon className="h-4 w-4" /> Filters
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-[200px] p-0">
            <DropdownMenuLabel>Filters</DropdownMenuLabel>
            <DropdownMenuSeparator className="mb-0" />
            <FilterSubMenuMultiselect
              label="Bottlenecks"
              options={jobBottleneckOptions}
              selected={jobQueryParams.bottlenecks}
              setSelected={(bottlenecks) => {
                handleUpdateView({
                  ...jobQueryParams,
                  bottlenecks,
                });
              }}
            />
            <FilterSubMenuMultiselect
              label="Difficulties"
              options={jobDifficultyOptions}
              selected={jobQueryParams.difficulties}
              setSelected={(difficulties) => {
                handleUpdateView({
                  ...jobQueryParams,
                  difficulties,
                });
              }}
            />
            <FilterSubMenuMultiselect
              label="Healths"
              options={jobHealthOptions}
              selected={jobQueryParams.healths}
              setSelected={(healths) => {
                handleUpdateView({
                  ...jobQueryParams,
                  healths,
                });
              }}
            />
            <FilterSubMenuMultiselect
              label="Job Statuses"
              options={jobStatusOptions}
              selected={jobQueryParams.jobStatuses}
              setSelected={(jobStatuses) => {
                handleUpdateView({
                  ...jobQueryParams,
                  jobStatuses,
                });
              }}
            />
            <FilterSubMenuMultiselect
              label="Owners"
              options={userOptions}
              selected={jobQueryParams.ownerIds}
              setSelected={(ownerIds) => {
                handleUpdateView({
                  ...jobQueryParams,
                  ownerIds,
                });
              }}
            />
            <FilterSubMenuMultiselect
              label="Requisition Statuses"
              options={requisitionStatusOptions}
              selected={jobQueryParams.requisitionStatuses}
              setSelected={(requisitionStatuses) => {
                handleUpdateView({
                  ...jobQueryParams,
                  requisitionStatuses,
                });
              }}
            />
            <FilterSubMenuMultiselect
              label="Urgencies"
              options={jobUrgencyOptions}
              selected={jobQueryParams.urgencies}
              setSelected={(urgencies) => {
                handleUpdateView({
                  ...jobQueryParams,
                  urgencies,
                });
              }}
            />
            <DropdownMenuItem
              className="p-0"
              onClick={() => {
                handleUpdateView({
                  ...jobQueryParams,
                  bottlenecks: [],
                  difficulties: [],
                  healths: [],
                  jobStatuses: [],
                  ownerIds: [],
                  requisitionStatuses: [],
                  urgencies: [],
                });
              }}
            >
              <DropdownMenuLabel className="font-medium">
                Clear all filters
              </DropdownMenuLabel>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button className="h-8" variant="outlineSecondary">
              <GroupIcon className="h-4 w-4" /> Group by
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="p-0">
            <DropdownMenuLabel>Grouping</DropdownMenuLabel>
            <DropdownMenuSeparator className="mb-0" />
            {groupByOptions.map((option) => (
              <DropdownMenuCheckboxItem
                key={option.label}
                checked={jobQueryParams.groupBy === option.value}
                onClick={(e) => {
                  e.preventDefault();
                  handleUpdateView({
                    ...jobQueryParams,
                    groupBy:
                      jobQueryParams.groupBy === option.value
                        ? undefined
                        : option.value,
                  });
                }}
                className="px-8"
              >
                {option.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <TableOrderByDropdown
          queryParams={jobQueryParams}
          setQueryParams={handleUpdateView}
          sortOptions={sortOptions}
        />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button className="h-8" variant="outlineSecondary">
              <MoreHorizontalIcon className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={() => {
                mixpanelUtils.trackButtonClick(
                  "Export Job Tracker",
                  MIXPANEL_SCREENS.JOB_TRACKER,
                );
                setIsExportJobTrackerConfirmationDialogOpen(true);
              }}
            >
              Export job tracker
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                mixpanelUtils.trackButtonClick(
                  "Export Job Tracker All Jobs",
                  MIXPANEL_SCREENS.JOB_TRACKER,
                );
                setIsExportJobTrackerAllJobsConfirmationDialogOpen(true);
              }}
            >
              Export job tracker - all jobs
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                mixpanelUtils.trackButtonClick(
                  "Export Requisitions",
                  MIXPANEL_SCREENS.JOB_TRACKER,
                );
                setIsExportRequisitionsConfirmationDialogOpen(true);
              }}
            >
              Export requisitions
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <ConfirmationDialog
          title="Export requisitions"
          description="A CSV of reqs, all lined up in rows, with dates and owners - off it goes! We'll email it to you, quick as can be. Click Export and check your inbox shortly."
          open={isExportRequisitionsConfirmationDialogOpen}
          onOpenChange={(open) => {
            if (!open) exportRequisitions.reset();
            setIsExportRequisitionsConfirmationDialogOpen(open);
          }}
          onConfirm={() => {
            exportRequisitions.mutate(undefined, {
              onSuccess: () => {
                exportRequisitions.reset();
                setIsExportRequisitionsConfirmationDialogOpen(false);
                successToast({
                  description: "Export queued - check your email shortly.",
                });
              },
            });
          }}
          confirmButtonText="Export"
          isLoading={exportRequisitions.isPending}
          isError={exportRequisitions.isError}
          errorMessage="Failed to queue export. Please try again."
        />
        <ConfirmationDialog
          title="Export job tracker"
          description="Your job tracker view, filters and all, exported to CSV at your call! We'll email it to you, row by row. Click Export and off it'll go!"
          open={isExportJobTrackerConfirmationDialogOpen}
          onOpenChange={(open) => {
            if (!open) exportJobTracker.reset();
            setIsExportJobTrackerConfirmationDialogOpen(open);
          }}
          onConfirm={() => {
            exportJobTracker.mutate(jobQueryParams, {
              onSuccess: () => {
                exportJobTracker.reset();
                setIsExportJobTrackerConfirmationDialogOpen(false);
                successToast({
                  description: "Export queued - check your email shortly.",
                });
              },
            });
          }}
          confirmButtonText="Export"
          isLoading={exportJobTracker.isPending}
          isError={exportJobTracker.isError}
          errorMessage="Failed to queue export. Please try again."
        />
        <ConfirmationDialog
          title="Export job tracker - all jobs"
          description="Every job in the book, reqs or none, exported to CSV for everyone! We'll email it to you, all jobs in tow. Click Export and off it'll go!"
          open={isExportJobTrackerAllJobsConfirmationDialogOpen}
          onOpenChange={(open) => {
            if (!open) exportJobTrackerAllJobs.reset();
            setIsExportJobTrackerAllJobsConfirmationDialogOpen(open);
          }}
          onConfirm={() => {
            exportJobTrackerAllJobs.mutate(undefined, {
              onSuccess: () => {
                exportJobTrackerAllJobs.reset();
                setIsExportJobTrackerAllJobsConfirmationDialogOpen(false);
                successToast({
                  description: "Export queued - check your email shortly.",
                });
              },
            });
          }}
          confirmButtonText="Export"
          isLoading={exportJobTrackerAllJobs.isPending}
          isError={exportJobTrackerAllJobs.isError}
          errorMessage="Failed to queue export. Please try again."
        />
      </Row>
    );
  };

  return render();
};
